import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, 
  Users, 
  Building2, 
  Smartphone, 
  FileText, 
  GitBranch, 
  BarChart3,
  Settings,
  PackageOpen,
  LayoutGrid,
  Activity,
  Terminal,
  Tag,
  ClipboardList,
  Database,
  ChevronDown,
  ChevronRight,
  Shield,
  Mail,
  UserCheck,
  ShieldCheck,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { useProductionCapabilities } from '@/lib/use-production-capabilities'

type NavItem = { name: string; href: string; icon: React.ElementType; superAdminOnly?: boolean }
type NavGroup = { group: string; icon: React.ElementType; children: NavItem[]; superAdminOnly?: boolean }
type NavEntry = NavItem | NavGroup

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'group' in entry
}

const navigation: NavEntry[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Produção', href: '/production', icon: ClipboardList },
  { name: 'Utilizadores', href: '/users', icon: Users },
  { name: 'Papéis', href: '/roles', icon: Shield },
  { name: 'Tenants', href: '/tenants', icon: Building2, superAdminOnly: true },
  { name: 'Dispositivos', href: '/devices', icon: Smartphone },
  { name: 'Atividade', href: '/activity', icon: Activity },
  { name: 'Telemetria', href: '/logs', icon: Terminal },
  { name: 'Formulários', href: '/forms', icon: FileText },
  { name: 'Submissões', href: '/submissions', icon: ClipboardList },
  {
    group: 'Dados Mestre',
    icon: Database,
    children: [
      { name: 'Clientes', href: '/clientes', icon: UserCheck },
      { name: 'Categorias', href: '/categories', icon: Tag },
      { name: 'Departamentos', href: '/departments', icon: Building2 },
      { name: 'Atributos', href: '/master-data/attributes', icon: Database },
    ],
  },
  {
    group: 'Workflows',
    icon: GitBranch,
    children: [
      { name: 'Definições', href: '/workflows', icon: GitBranch },
      { name: 'Fluxos Ativos', href: '/workflows/instances', icon: ClipboardList },
    ],
  },
  { name: 'Relatórios', href: '/reports', icon: BarChart3 },
  { name: 'Ecrã Mobile', href: '/home-cards', icon: LayoutGrid },
  { name: 'Versões', href: '/releases', icon: PackageOpen },
  {
    group: 'Definições',
    icon: Settings,
    children: [
      { name: 'Perfil', href: '/settings/profile', icon: Settings },
      { name: 'SMTP / Email', href: '/settings/smtp', icon: Mail },
      { name: 'Etiquetas', href: '/settings/labels', icon: Tag },
      { name: 'Auditoria', href: '/settings/audit', icon: ShieldCheck },
    ],
  },
]


function SidebarGroup({ entry }: { entry: NavGroup }) {
  const location = useLocation()
  const isChildActive = entry.children.some(c => location.pathname.startsWith(c.href))
  const [open, setOpen] = useState(isChildActive)

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isChildActive ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
        )}
      >
        <entry.icon className="h-5 w-5 shrink-0" />
        <span className="flex-1 text-left">{entry.group}</span>
        {open ? (
          <ChevronDown className="h-4 w-4 opacity-60" />
        ) : (
          <ChevronRight className="h-4 w-4 opacity-60" />
        )}
      </button>
      {open && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
          {entry.children.map(child => (
            <NavLink
              key={child.name}
              to={child.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-100'
                )
              }
            >
              <child.icon className="h-4 w-4" />
              {child.name}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export function Sidebar() {
  const { user } = useAuth()
  const capabilities = useProductionCapabilities()
  const isSuperAdmin = user?.roles?.some(r => r.name === 'SUPER_ADMIN') || user?.permissions?.includes('*') || false
  const visibleNav = navigation.filter(e =>
    (!e.superAdminOnly || isSuperAdmin) &&
    (isGroup(e) || e.href !== '/production' ||
      (!capabilities.isError && capabilities.data?.enabled && capabilities.data.mode !== 'off'))
  )

  return (
    <aside className="w-64 border-r bg-white">
      <div className="flex h-16 items-center border-b px-6">
        <h1 className="text-xl font-bold text-primary-600">Shopfloor Hub</h1>
      </div>
      <nav className="space-y-1 p-4">
        {visibleNav.map((entry) =>
          isGroup(entry) ? (
            <SidebarGroup key={entry.group} entry={entry} />
          ) : (
            <NavLink
              key={entry.name}
              to={entry.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )
              }
            >
              <entry.icon className="h-5 w-5" />
              {entry.name}
            </NavLink>
          )
        )}
      </nav>
    </aside>
  )
}
